import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, XIcon } from './icons';
import type { ChatProductItem } from '../providers/types';

interface ProductSuggestionFocusProps {
  /** Latest product suggested by the advisor, or null when none yet. */
  suggestion: ChatProductItem | null;
  /** Whether the focus card is expanded (auto-set true on a new suggestion). */
  isExpanded: boolean;
  /** Toggle expanded/collapsed. */
  onToggle: () => void;
  /**
   * Open the product detail for this suggestion (in-call, no A/V interruption),
   * on the suggested variant when the card names one.
   */
  onViewDetails: (productId: number, variantId?: number) => void;
  /**
   * Monotonic counter that increments on every new suggestion. Used as the
   * animation key so the gold highlight re-plays each time — an unobtrusive
   * in-panel cue (never a toast over the video).
   */
  pulseKey: number;
  /**
   * The assigned advisor's real name (currentSeller.full_name). Shown in the
   * "Suggested by …" label; falls back to "your advisor" (the app's established
   * term, cf. "Waiting for advisor to join…") when the seller isn't bound yet.
   */
  advisorName?: string;
}

function numericId(id: number | string): number | null {
  const n = typeof id === 'number' ? id : Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Soft-focus suggestion card. Lives at the top of the cream products sidebar so
 * a seller-suggested product surfaces WITHOUT switching the call to chat mode
 * (A/V stays live). Theme: brand-cream (#F8F6F0) / brand-gold (#C9A961).
 */
export function ProductSuggestionFocus({
  suggestion,
  isExpanded,
  onToggle,
  onViewDetails,
  pulseKey,
  advisorName,
}: ProductSuggestionFocusProps) {
  if (!suggestion) return null;

  const id = numericId(suggestion.id);

  return (
    <div className="shrink-0 border-b border-brand-gold/20 bg-brand-cream">
      {/* Header row — always visible; acts as the unobtrusive "new suggestion" cue. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="w-full flex items-center gap-3 px-6 py-3 text-left group"
      >
        {/* Gold pulse ring — replays on each new suggestion via pulseKey. */}
        <span className="relative flex items-center justify-center">
          <AnimatePresence>
            <motion.span
              key={pulseKey}
              initial={{ scale: 0.6, opacity: 0.7 }}
              animate={{ scale: 2.2, opacity: 0 }}
              transition={{ duration: 1.6, ease: 'easeOut' }}
              className="absolute h-5 w-5 rounded-full bg-brand-gold/40"
            />
          </AnimatePresence>
          <SparklesIcon size={16} className="relative text-brand-gold" strokeWidth={1.75} />
        </span>

        <span className="flex-1 min-w-0">
          <span className="block text-[10px] uppercase tracking-[0.18em] text-brand-gold font-sans">
            Suggested by {advisorName || 'your advisor'}
          </span>
          <span className="block truncate text-sm text-brand-navy font-serif">
            {suggestion.name || 'A product for you'}
          </span>
        </span>

        <span className="text-brand-navy/50 text-xs font-sans tracking-wide shrink-0">
          {isExpanded ? 'Hide' : 'View'}
        </span>
      </button>

      {/* Expanded detail — auto-open on new suggestion (Q3). */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="suggestion-detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <motion.div
              key={`glow-${pulseKey}`}
              initial={{ boxShadow: '0 0 0 0 rgba(201,169,97,0.55)' }}
              animate={{ boxShadow: '0 0 0 0 rgba(201,169,97,0)' }}
              transition={{ duration: 1.8, ease: 'easeOut' }}
              className="mx-6 mb-4 flex gap-4 rounded-sm border border-brand-gold/30 bg-white p-3"
            >
              <div className="h-20 w-16 shrink-0 overflow-hidden rounded-sm bg-brand-cream">
                {suggestion.imageUrl ? (
                  <img
                    src={suggestion.imageUrl}
                    alt={suggestion.name || 'Suggested product'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <SparklesIcon size={18} className="text-brand-gold/50" />
                  </div>
                )}
              </div>

              <div className="flex min-w-0 flex-1 flex-col justify-between">
                <div className="min-w-0">
                  <h3 className="truncate text-base text-brand-navy font-serif">
                    {suggestion.name || 'A product for you'}
                  </h3>
                  <p className="mt-0.5 text-sm text-brand-navy/70 font-sans">
                    {suggestion.price ?? 'Price on request'}
                  </p>
                </div>

                {id !== null && (
                  <button
                    type="button"
                    onClick={() => onViewDetails(id, suggestion.variantId)}
                    className="mt-2 self-start px-4 py-2 bg-brand-navy text-white text-[11px] tracking-[0.15em] uppercase font-sans hover:bg-brand-gold transition-colors duration-500 rounded-sm"
                  >
                    View Details
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={onToggle}
                aria-label="Collapse suggestion"
                className="self-start p-1 text-brand-navy/40 hover:text-brand-navy/70 transition-colors"
              >
                <XIcon size={16} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
